import {logger} from 'yougile-util'
import {DataObjectId} from 'yougile-model'
import {Disposer} from '@api/model/ui-registry'
import {globals} from '../extensions/globals-base'
import {Context} from './context'

/**
 * Validation methods such as `onMove` can return an object of this type instead of a boolean to additionally specify behavior.
 */
export interface ValidationDetails {
  success: boolean
  /**
   * After onMove validation fails, this property can specify behavior of taskDropHandler to avoid of performing workaround for drag and drop.
   * TODO: Should remove this property in the future, when the workaround will be removed.
   */
  avoidMoveTreeNode?: boolean
  /**
   * Optional field in dev purposes. If the validation fails, this property can be used to specify a reason for the failure.
   */
  reason?: string
}

export class ContextObject {
  public context: Context
  public global: any

  /**
   * Dispose handlers. Used to clean up resources when the script is unloaded.
   * To add a handler, use the `addDisposeHandler` method.
   * @private
   */
  private disposeHandlers: Disposer[] = []

  /**
   * Version of the script. The version should be overridden in the child class.
   * It is used by extensions to check if the required features are supported.
   * For example, if the script uses a new API method, it can check if the current version is less than the required version.
   * In this way, extensions can maintain backward compatibility.
   * To compare versions in an extension, use the `UI.isVersionLessOrEqual` method (e.g. `UI.isVersionLessOrEqual('1.0.0')`).
   */
  public version = '0.0.0'

  /**
   * Check if the current version is less or equal to the input version.
   * @param inputVersion
   */
  public isVersionLessOrEqual(inputVersion: string) {
    const current = this.version.split('.').map(Number)
    const input = inputVersion.split('.').map(Number)

    for (let i = 0; i < Math.max(current.length, input.length); i++) {
      const currentPart = current[i] || 0
      const inputPart = input[i] || 0

      if (inputPart < currentPart) {
        return true
      } else if (inputPart > currentPart) {
        return false
      }
    }

    return true
  }

  /**
   * This method can be overridden in the child class.
   */
  public afterLoad(): void {
    //
  }

  /**
   * This method can be overridden in the child class.
   */
  public beforeUnload(): void {
    //
  }

  constructor(context: Context, global: any) {
    this.context = context
    this.global = global
  }

  public static registered: Map<DataObjectId, Map<string, ContextObject>> =
    new Map()

  public static register(instance: ContextObject): void {
    const companyId = instance.context.box.companyId
    if (!this.registered.has(companyId)) {
      this.registered.set(companyId, new Map())
    }
    if (this.registered.get(companyId).has(instance.context.scriptName)) {
      throw new Error('Script already added')
    }
    this.registered.get(companyId).set(instance.context.scriptName, instance)
    instance.afterLoad()
  }

  public static unregister(companyId: DataObjectId, name: string): void {
    if (this.registered.has(companyId)) {
      const instance = this.registered.get(companyId).get(name)
      if (instance) {
        instance.beforeUnload()
        instance.disposeHandlers.forEach(handler => handler())
      }
      this.registered.get(companyId).delete(name)
    }
  }

  public static unregisterAll(): void {
    for (const companyId of this.registered.keys()) {
      for (const instance of this.registered.get(companyId).values()) {
        instance.beforeUnload()
        instance.disposeHandlers.forEach(handler => handler())
      }
      this.registered.get(companyId).clear()
    }
  }

  /**
   * Calls cb with every registered object and returns true if all context objects passed validation, or false if at least one context object failed validation.
   * For optional detailed validation result, use `validateAllCurrent` method.
   * @param cb
   */
  public static forEachCurrent(
    cb: (i: ContextObject) => boolean | ValidationDetails
  ): boolean {
    const companyId = globals.getCompanyId()
    if (this.registered.has(companyId)) {
      for (const [, v] of this.registered.get(companyId)) {
        try {
          const proceed = cb(v)
          /* We maintain backward compatibility with the response format as an object, which can be returned by handlers for processing in the validateAllCurrent method, but which may also "leak" into this method. */
          if (
            typeof proceed === 'object' &&
            typeof proceed.success === 'boolean'
          ) {
            if (!proceed.success) {
              return false
            }
            continue
          }
          if (typeof proceed !== 'undefined' && !proceed) {
            return false
          }
        } catch (error) {
          logger.error('Error in context object callback:', error)
          return false
        }
      }
    }
    return true
  }

  /**
   * Calls cb with every registered object and returns ValidationDetails object that contains `success` prop and optional additional information about validation result such (see ValidationDetails).
   * Returns true if all context objects passed validation, or false if at least one context object failed validation.
   * @param cb
   * @returns {ValidationDetails} Returns ValidationResult object that contains `success` property, which is `true` if all context objects passed validation, or `false` if at least one context object failed validation.
   */
  public static validateAllCurrent(
    cb: (i: ContextObject) => boolean | ValidationDetails
  ): ValidationDetails {
    const companyId = globals.getCompanyId()
    if (this.registered.has(companyId)) {
      for (const [, v] of this.registered.get(companyId)) {
        const validationResult = cb(v)
        if (
          typeof validationResult === 'object' &&
          typeof validationResult.success === 'boolean'
        ) {
          if (!validationResult.success) {
            return validationResult
          }
          continue
        }
        if (typeof validationResult !== 'undefined' && !validationResult) {
          return {
            success: false
          }
        }
      }
    }
    return {
      success: true
    }
  }

  /**
   * Map all current context objects to specific type.
   * @param cb
   */
  public static mapCurrent<T>(cb: (i: ContextObject) => T): T[] {
    const companyId = globals.getCompanyId()
    const result: T[] = []
    if (this.registered.has(companyId)) {
      for (const [, v] of this.registered.get(companyId)) {
        result.push(cb(v))
      }
    }
    return result
  }

  /**
   * Get all current context objects as an array.
   */
  public static getCurrentAsArray(): ContextObject[] {
    const companyId = globals.getCompanyId()
    const result: ContextObject[] = []
    if (this.registered.has(companyId)) {
      for (const [, v] of this.registered.get(companyId)) {
        result.push(v)
      }
    }
    return result
  }

  /**
   * Add a dispose handler. The handler will be called when the script is unloaded.
   * It returns a disposer that can be used to remove the handler.
   * @param handler
   * @returns Disposer
   */
  public addDisposeHandler(handler: Disposer): Disposer {
    this.disposeHandlers.push(handler)

    return () => {
      this.disposeHandlers = this.disposeHandlers.filter(h => h !== handler)
    }
  }
}
